import {Component, OnInit, ViewChild} from '@angular/core';
import {CheckoutService} from '../shared-core/services/checkout.service';
import {FormBuilder, Validators} from '@angular/forms';
import {hasError, touchFormControls, VistekValidation} from '../shared-core/services/validation.service';
import {
    Address,
    AddressType,
    CartOrder,
    CartOrderAddress,
    CartOrderProfile,
    CheckoutData
} from '../shared-core/models/checkout-data.model';
import {AddressEditComponent} from './address-edit/address-edit.component';
import {AuthService} from '../shared-core/services/auth.service';
import {ComponentMessagingService} from '../shared-core/services/component-messaging.service';
import {MessageTopicsEnum} from '../shared-core/models/component-message.model';
import {PayerAuthService} from "../shared-core/services/payer-auth.service";

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
})
export class CheckoutComponent implements OnInit {
  AddressType = AddressType;
  @ViewChild('shippingEditor') shippingEditor: AddressEditComponent;
  @ViewChild('billingEditor') billingEditor: AddressEditComponent;
  private billingIsShipping: boolean;
  isLoggedIn: boolean;
  public isBuyingGiftCard = false;
  public isDigitalOrder = false;
  
  billingAddress: Address;
  get BillingIsShipping(): boolean {
    return this.billingIsShipping;
  }

  set BillingIsShipping(value: boolean) {
    this.billingIsShipping = value;
    const billing = { IsBillingShipping: value } as CartOrderAddress;
    if (value) {
      this.addressToBilling(this.shippingAddress, billing);
    }
    this.service.updateBillingIsShipping(billing).subscribe(x => {
      this.billingAddress = this.service.mapAddress(x.Billing, AddressType.Billing);
    });
  }

  isEmailCompleted = false;
  model: CheckoutData;
  shippingAddress: Address;
  cartOrder: CartOrder;

  constructor(
    public service: CheckoutService,
    public payerAuthService: PayerAuthService,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private messaging: ComponentMessagingService
  ) {}

  emailForm = this.formBuilder.group({
    email: [
      '',
      [Validators.required, Validators.maxLength(80), VistekValidation.email()],
      [VistekValidation.emailExists(this.authService)],
    ],
  });

  emailHasError = (formControlName: string, error: string | string[] = null): boolean => {
    return hasError(this.emailForm, formControlName, error);
  };

  ngOnInit(): void {
    this.service.LoadCheckoutData();
    this.service.CheckoutDataSubject.subscribe((checkoutData) => {
      this.model = checkoutData;
      this.isLoggedIn = this.model?.CartOrder?.LoggedIn;
      if (!checkoutData?.CartOrder) {
        return;
      }
      this.billingIsShipping = checkoutData.CartOrder.Billing.IsBillingShipping;
      this.cartOrder = checkoutData.CartOrder;

      this.shippingAddress = this.service.mapAddress(this.cartOrder.Shipping, AddressType.Shipping);
      this.billingAddress = this.service.mapAddress(this.cartOrder.Billing, AddressType.Billing);
      
      this.emailForm.patchValue({
        email: this.cartOrder.Email,
      });

      this.isEmailCompleted = !!this.cartOrder.Email && !this.cartOrder.LoginRequired;

      this.isBuyingGiftCard = checkoutData.CartItems.some(x => x.Webcode.startsWith('EGIFTVAR') || x.Webcode.startsWith('GIFTVAR'));
      this.isDigitalOrder = checkoutData.CartItems.every((x => x.IsDigitalItem));
    });
  }
  
  public updateShippingAddress(): void {
    if (!this.shippingEditor.isValid()) {
      return;
    }

    this.shippingAddress = this.shippingEditor.getAddress();

    if (!this.isLoggedIn) {
      touchFormControls(this.emailForm);
      if (!this.emailForm.valid) {
        return;
      }
    }
    if (!this.shippingEditor.valid) {
      return;
    }

    const cartOrder = this.cartOrder;
    if (!this.isLoggedIn) {
      cartOrder.Email = this.emailForm.value.email;
      (window as any).LogRocket?.track('checkoutEmailSaving', {
        email: cartOrder.Email,
      });
    }
    this.isEmailCompleted = true;

    cartOrder.Shipping ??= { } as CartOrderAddress;
    cartOrder.Shipping.PostalCode = this.shippingAddress.PostalCodeZip;
    cartOrder.Shipping.FirstName = this.shippingAddress.FirstName;
    cartOrder.Shipping.LastName = this.shippingAddress.LastName;
    cartOrder.Shipping.Address = this.shippingAddress.Address_;
    cartOrder.Shipping.Address2 = this.shippingAddress.Address2;
    cartOrder.Shipping.StateProvinceID = this.shippingAddress.StateProvinceID;
    cartOrder.Shipping.StateProvince = this.shippingAddress.StateProvinceName;
    cartOrder.Shipping.City = this.shippingAddress.City;
    cartOrder.Shipping.CountryID = this.shippingAddress.CountryID;
    cartOrder.Shipping.Country = this.shippingAddress.CountryName;
    cartOrder.Shipping.Company = this.shippingAddress.Company;
    cartOrder.Shipping.Phone = this.shippingAddress.Phone;

    var profile = { 
      FirstName: this.shippingAddress.FirstName,
      LastName: this.shippingAddress.LastName,
      Company: this.shippingAddress.Company,
      Phone: this.shippingAddress.Phone,
      Email: this.emailForm.get('email').value
    } as CartOrderProfile;

      // Set this to true so that the address editor component changes to the address view component.
      // Later, in the `subscribe`, it will switch back if there was an error.
      cartOrder.ShippingAddressCompleted = true;
    
      this.service.updateShippingAddress(profile, cartOrder.Shipping).subscribe(x => {
        this.cartOrder.ShippingAddressCompleted = x.CartOrder.ShippingAddressCompleted;
        this.shippingAddress = this.service.mapAddress(x.CartOrder.Shipping, AddressType.Shipping);     
    });
  }

  continueToPayment(): void {
    if (!this.billingEditor.isValid()) {
      return;
    }

    // Set this to true so that the address editor component changes to the address view component.
    // Later, in the `subscribe`, it will switch back if there was an error.
    this.cartOrder.BillingAddressCompleted = true;
    
    this.billingAddress = this.billingEditor.getAddress();
    const billing = { IsBillingShipping: false } as CartOrderAddress;
    this.addressToBilling(this.billingAddress, billing);
    
    this.service.updateBillingAddress(billing).subscribe(x => {
      this.cartOrder.BillingAddressCompleted = x.CartOrder.BillingAddressCompleted;
      this.billingAddress = this.service.mapAddress(x.CartOrder.Billing, AddressType.Billing);
    });
  }

  addressToBilling(address: Address, billing: CartOrderAddress): void {
    billing.PostalCode = address.PostalCodeZip;
    billing.FirstName = address.FirstName;
    billing.LastName = address.LastName;
    billing.Address = address.Address_;
    billing.Address2 = address.Address2;
    billing.StateProvinceID = address.StateProvinceID;
    billing.StateProvince = address.StateProvinceName;
    billing.City = address.City;
    billing.CountryID = address.CountryID;
    billing.Country = address.CountryName;
    billing.Company = address.Company;
    billing.Phone = address.Phone;
  }

  editMyAccount(event: UIEvent) {
    this.isEmailCompleted = false;
    this.cartOrder.ShippingAddressCompleted = false;
    event.preventDefault();
  }

  editBilling(event: UIEvent) {
    this.cartOrder.BillingAddressCompleted = false;
    event.preventDefault();
  }

  onAccountExists(event: UIEvent) {
    event.preventDefault();
    this.messaging.sendMessage(MessageTopicsEnum.Login, 'login');
  }
}
